import {
  useCallback,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type MouseEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { FileText, Upload } from "lucide-react";
import { Button, Input, Label } from "@cliently/ui";
import type { IntakeField } from "@cliently/types";

const FILE_ACCEPT = ".pdf,image/*";



const isAcceptedIntakeFile = (file: File): boolean => {
  if (file.type === "application/pdf") return true;
  return file.type.startsWith("image/");
}

interface DocumentDropZoneProps {
  fieldId: string;
  selectedFile: File | null | undefined;
  hasExistingValue: boolean;
  onSelect: (file: File | null) => void;
}



const DocumentDropZone = ({
  fieldId,
  selectedFile,
  hasExistingValue,
  onSelect,
}: DocumentDropZoneProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const dragDepth = useRef(0);

  const applyFile = useCallback(
    (file: File | null) => {
      onSelect(file);
    },
    [onSelect],
  );

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    if (f && !isAcceptedIntakeFile(f)) {
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    applyFile(f);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    dragDepth.current = 0;
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0] ?? null;
    if (!f) return;
    if (!isAcceptedIntakeFile(f)) return;
    applyFile(f);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current += 1;
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) {
      dragDepth.current = 0;
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const clearFile = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    applyFile(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const openPicker = () => inputRef.current?.click();

  const showFileName =
    selectedFile?.name ||
    (hasExistingValue ? t("intake.documentAttached") : null);

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        id={`intake-doc-${fieldId}`}
        type="file"
        accept={FILE_ACCEPT}
        className="sr-only"
        onChange={handleInputChange}
      />

      <div
        role="button"
        tabIndex={0}
        aria-label={t("intake.dropZoneHint")}
        onClick={openPicker}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openPicker();
          }
        }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={
          "border-input bg-background/50 focus-visible:ring-ring flex min-h-[120px] cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-5 text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background " +
          (isDragging
            ? "border-primary bg-primary/10 ring-primary/40 ring-2"
            : "hover:border-primary/60 hover:bg-muted/30")
        }
      >
        <div
          className={
            "bg-primary/15 text-primary flex h-11 w-11 items-center justify-center rounded-full " +
            (isDragging ? "scale-105" : "")
          }
        >
          <Upload className="h-5 w-5" aria-hidden />
        </div>
        <div className="space-y-0.5">
          <p className="text-foreground text-sm font-medium">
            {t("intake.dropZoneHint")}
          </p>
          <p className="text-muted-foreground text-xs">
            {t("intake.dropZoneFormats")}
          </p>
        </div>
      </div>

      {showFileName ? (
        <div className="border-input bg-muted/20 flex items-center gap-2 rounded-md border px-3 py-2">
          <FileText className="text-primary h-4 w-4 shrink-0" aria-hidden />
          <span className="text-foreground min-w-0 flex-1 truncate text-sm">
            {selectedFile?.name ?? showFileName}
          </span>
          {selectedFile ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground h-8 shrink-0 px-2 text-xs"
              onClick={clearFile}
            >
              {t("intake.removeFile")}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

interface IntakeFormFieldsProps {
  fields: IntakeField[];
  values: Record<string, string>;
  onChange: (id: string, value: string) => void;
  errors?: Record<string, string>;
  fileInputs?: Record<string, File | null>;
  onFileChange?: (id: string, file: File | null) => void;
}



export const IntakeFormFields = ({
  fields,
  values,
  onChange,
  errors,
  fileInputs,
  onFileChange,
}: IntakeFormFieldsProps) => {
  if (!fields.length) return null;

  return (
    <div className="space-y-3">
      {fields.map((field) => (
        <div key={field.id} className="space-y-1">
          <Label
            className="text-sm"
            htmlFor={
              field.type === "document"
                ? `intake-doc-${field.id}`
                : `field-${field.id}`
            }
          >
            {field.label}
            {field.required && <span className="text-destructive ml-1">*</span>}
          </Label>

          {field.type === "document" ? (
            <DocumentDropZone
              fieldId={field.id}
              selectedFile={fileInputs?.[field.id] ?? null}
              hasExistingValue={Boolean(values[field.id]?.trim())}
              onSelect={(file) => {
                onFileChange?.(field.id, file);
                onChange(field.id, file?.name ?? "");
              }}
            />
          ) : field.type === "textarea" ? (
            <textarea
              id={`field-${field.id}`}
              className="border-input bg-background focus-visible:ring-ring flex min-h-[80px] w-full rounded-md border px-3 py-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1"
              value={values[field.id] || ""}
              onChange={(e) => onChange(field.id, e.target.value)}
              placeholder={field.label}
            />
          ) : field.type === "select" ? (
            <select
              id={`field-${field.id}`}
              className="border-input bg-background focus-visible:ring-ring flex h-9 w-full rounded-md border px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1"
              value={values[field.id] || ""}
              onChange={(e) => onChange(field.id, e.target.value)}
            >
              <option value="">Sélectionner...</option>
              {(field.options || []).filter(Boolean).map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          ) : (
            <Input
              id={`field-${field.id}`}
              type={
                field.type === "email"
                  ? "email"
                  : field.type === "phone"
                    ? "tel"
                    : field.type === "number"
                      ? "number"
                      : field.type === "date"
                        ? "date"
                        : "text"
              }
              value={values[field.id] || ""}
              onChange={(e) => onChange(field.id, e.target.value)}
              placeholder={field.label}
            />
          )}

          {errors?.[field.id] && (
            <p className="text-destructive text-xs">{errors[field.id]}</p>
          )}
        </div>
      ))}
    </div>
  );
}
